import {Alert, Button, Snackbar} from '@mui/material'
import {useEffect, useState} from 'react'
import type {FC, ReactElement} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {markRecoveryReload} from '@shared/utils/recoveryReload'

const CHUNK_ERROR_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i

const RELOAD_GUARD_KEY = 'mbai_chunk_reload_attempted_at'

// Minimum gap between automatic reloads. Long-lived tabs (factory clients
// keep the app open for days) cross MANY deploys — a once-per-session guard
// permanently disables auto-recovery after the first deploy the tab
// survives, dumping every later deploy onto the crash screen. A time-window
// guard instead allows one silent reload per failure episode while still
// preventing a tight reload loop against a genuinely broken deploy: two
// failures inside the window fall back to the manual prompt.
const RELOAD_RETRY_WINDOW_MS = 30_000

// Proactive deploy detection: poll the build id the server currently serves
// (dist/version.json, written by vite.config.ts) and compare against the id
// baked into this running bundle. On mismatch, show the banner BEFORE the
// user ever hits a missing chunk, so they can finish and save first, then
// reload on their own schedule. Deploys keep the assets of recent builds
// (config/swa/retainPreviousAssets.ts), so a stale tab keeps loading its
// routes meanwhile; the chunk-error auto-reload below is the safety net for
// a tab older than that.
const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000

const fetchServedBuildId = async (): Promise<string | null> => {
  try {
    const response = await fetch('/version.json', {cache: 'no-store'})
    if (!response.ok) return null
    const data = (await response.json()) as {buildId?: string}
    return data.buildId ?? null
  } catch {
    return null
  }
}

/**
 * Mounted once above the top-level ErrorBoundary (see App.tsx) so it survives
 * any route/subtree crash.
 */
const NewVersionAvailableBanner: FC = (): ReactElement | null => {
  const [t] = useCustomTranslation('errors')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const ownBuildId = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : undefined
    // Dev server has no version.json and no stale-chunk problem.
    if (!import.meta.env.PROD || !ownBuildId) return undefined

    let cancelled = false
    const checkVersion = async (): Promise<void> => {
      const servedBuildId = await fetchServedBuildId()
      if (!cancelled && servedBuildId && servedBuildId !== ownBuildId) {
        setVisible(true)
      }
    }

    const intervalId = setInterval(checkVersion, VERSION_POLL_INTERVAL_MS)
    // Factory tabs sit idle for days — checking on focus catches a deploy
    // exactly when the user comes back, before their first click.
    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible') void checkVersion()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [])

  useEffect(() => {
    const recoverFromChunkError = (event: Event): void => {
      event.preventDefault()
      const lastAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY))
      if (lastAttempt && Date.now() - lastAttempt < RELOAD_RETRY_WINDOW_MS) {
        // Auto-reloaded moments ago and it's still failing — show the manual
        // prompt instead of looping.
        setVisible(true)
        return
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
      // Deploys keep the assets of recent builds (AB#6757), so this only runs
      // for a tab older than that window. Unlike the banner's Refresh, nobody
      // asked for this reload: it keeps the unsaved-changes prompt, and a user
      // who answers it with Stay gets the banner to reload when ready.
      setVisible(true)
      window.location.reload()
    }

    const handleRejection = (event: PromiseRejectionEvent): void => {
      const message = event.reason?.message ?? String(event.reason ?? '')
      if (CHUNK_ERROR_PATTERN.test(message)) {
        recoverFromChunkError(event)
      }
    }

    window.addEventListener('vite:preloadError', recoverFromChunkError)
    window.addEventListener('unhandledrejection', handleRejection)
    return () => {
      window.removeEventListener('vite:preloadError', recoverFromChunkError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])

  if (!visible) return null

  return (
    <Snackbar open anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
      <Alert
        severity="info"
        variant="filled"
        sx={{color: 'common.white'}}
        onClose={() => setVisible(false)}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              markRecoveryReload()
              window.location.reload()
            }}
          >
            {t('refresh')}
          </Button>
        }
      >
        {t('new_version_available')}
      </Alert>
    </Snackbar>
  )
}

export default NewVersionAvailableBanner
